/**
 * Career Service
 * Handles AI Career Toolkit API calls
 */

const API_BASE_URL = import.meta.env.VITE_BACKEND_URL || 'http://localhost:5000';

/**
 * Analyze career path and get skill gap recommendations
 * @param {Object} params - Analysis parameters
 * @param {string} params.userId - User ID (required)
 * @param {string} params.jobDomain - Job domain (required)
 * @param {string} params.jobTitle - Job title (optional)
 * @param {string} params.currentSkills - Current skills (optional)
 * @returns {Promise<Object>} Career analysis result
 */
export const analyzeCareerPath = async ({ userId, jobDomain, jobTitle, currentSkills }) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/career/analyze`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        userId,
        jobDomain,
        jobTitle: jobTitle || '',
        currentSkills: currentSkills || ''
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to analyze career path');
    }

    return data;

  } catch (error) {
    console.error('❌ Career analysis error:', error);
    throw error;
  }
};

/**
 * Check career toolkit API health
 * @returns {Promise<Object>} Health status
 */
export const checkCareerApiHealth = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/career/health`);
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('❌ Health check error:', error);
    throw error;
  }
};

/**
 * Generate AI Career Map - personalized career progression roadmap
 * @param {Object} params - Career map parameters
 * @param {string} params.userId - User ID (required)
 * @param {string} params.currentJobTitle - Current job title (required)
 * @param {string} params.dreamJobTitle - Dream job title (required)
 * @param {string} params.currentSkills - Current skills (required)
 * @param {string} params.experienceRange - Years of experience (required)
 * @returns {Promise<Object>} Career roadmap result
 */
export const generateCareerMap = async ({ userId, currentJobTitle, dreamJobTitle, currentSkills, experienceRange }) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/career/career-map`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        userId,
        currentJobTitle,
        dreamJobTitle,
        currentSkills,
        experienceRange
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to generate career map');
    }

    return data;

  } catch (error) {
    console.error('❌ Career map generation error:', error);
    throw error;
  }
};

/**
 * Generate MCQ Test - AI-generated role-specific assessment
 * @param {Object} params - MCQ test parameters
 * @param {string} params.userId - User ID (required)
 * @param {string} params.jobTitle - Job title (required)
 * @param {string} [params.skills] - Skills to focus on (optional)
 * @param {string} params.difficultyLevel - Beginner, Intermediate, or Advanced (required)
 * @returns {Promise<Object>} MCQ test with questions
 */
export const generateMCQTest = async ({ userId, jobTitle, skills, difficultyLevel }) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/career/mcq-test`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        userId,
        jobTitle,
        skills,
        difficultyLevel
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to generate MCQ test');
    }

    return data;

  } catch (error) {
    console.error('❌ MCQ test generation error:', error);
    throw error;
  }
};

/**
 * Get Job Domains - Fetch all available job domains from Firestore
 * @returns {Promise<Object>} List of job domains
 */
export const getJobDomains = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/career/job-domains`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to fetch job domains');
    }

    return data;

  } catch (error) {
    console.error('❌ Job domains fetch error:', error);
    throw error;
  }
};
